import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiTarget, FiAward, FiTrendingUp } = FiIcons;

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: FiTarget,
      title: 'Our Mission',
      description: 'To deliver exceptional construction services that exceed client expectations while maintaining the highest standards of safety and quality.'
    },
    {
      icon: FiUsers,
      title: 'Our Team',
      description: 'Experienced professionals with diverse expertise in construction, engineering, and project management working together.'
    },
    {
      icon: FiAward,
      title: 'Quality First',
      description: 'We never compromise on quality. Every project is executed with precision and attention to detail.'
    },
    {
      icon: FiTrendingUp,
      title: 'Innovation',
      description: 'We embrace new technologies and methods to deliver better results and more efficient construction processes.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>About BuildCorp - Leading Construction Company | 25+ Years Experience</title>
        <meta name="description" content="Learn about BuildCorp's 25+ years of construction expertise. Professional team delivering quality residential, commercial, and industrial projects." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-navy-900 text-white py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1920&h=600&fit=crop)'
          }}
        ></div>
        <div className="relative container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold mb-4">About BuildCorp</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Building excellence for over 25 years with a commitment to quality, innovation, and customer satisfaction.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-navy-900 mb-6">Our Story</h2>
              <p className="text-gray-600 mb-6">
                Founded in 1999, BuildCorp has grown from a small local contractor to one of the region's most trusted construction companies. Our journey began with a simple mission: to build quality structures that stand the test of time.
              </p>
              <p className="text-gray-600 mb-6">
                Over the years, we have successfully completed over 500 projects, ranging from residential homes to large commercial complexes. Our commitment to excellence and customer satisfaction has earned us a reputation as a reliable partner in construction.
              </p>
              <p className="text-gray-600">
                Today, we continue to evolve and adapt to new technologies and methodologies while maintaining our core values of quality, integrity, and professionalism.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-2 gap-4"
            >
              <img 
                src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=300&h=250&fit=crop"
                alt="Construction site"
                className="w-full rounded-lg"
              />
              <img 
                src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=300&h=250&fit=crop"
                alt="Construction worker"
                className="w-full rounded-lg mt-8"
              />
              <img 
                src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=300&h=250&fit=crop"
                alt="Building"
                className="w-full rounded-lg -mt-8"
              />
              <img 
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=300&h=250&fit=crop"
                alt="Modern building"
                className="w-full rounded-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Our Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These core values guide everything we do and define who we are as a company.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg text-center"
              >
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-primary-500" />
                </div>
                <h3 className="text-xl font-semibold text-navy-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Leadership Team</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Meet the experienced professionals who lead our company and drive our success.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'John Smith',
                position: 'CEO & Founder',
                image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop',
                description: '25+ years in construction industry'
              },
              {
                name: 'Sarah Johnson',
                position: 'Project Manager',
                image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop',
                description: 'Expert in large-scale project management'
              },
              {
                name: 'Michael Brown',
                position: 'Chief Engineer',
                image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop',
                description: 'Structural engineering specialist'
              }
            ].map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <img 
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-semibold text-navy-900 mb-2">{member.name}</h3>
                <p className="text-primary-500 font-medium mb-2">{member.position}</p>
                <p className="text-gray-600">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-navy-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Choose BuildCorp?</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              We are the leader in the architecture industry. We are The Leader In The Architecture.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              'Professional Team',
              'Quality Materials',
              'On-Time Delivery',
              'Budget-Friendly',
              '24/7 Support',
              'Licensed & Insured'
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-center space-x-3"
              >
                <div className="bg-primary-500 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span className="text-lg">{feature}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default About;